// Filter translations by client and fallback languages
import { isNil, pick } from 'lodash';
import { createI18n } from 'vue-i18n';
import momentLib from 'moment-timezone';
import numeral from 'numeral';
import i18nconfig from '../../config/static-i18n-config';

// Filter translations by client and fallback languages
const i18n = createI18n({
    // TODO: fix this
    locale: i18nconfig.defaultLocale, // client-specific. Overwritten on context load
    fallbackLocale: i18nconfig.fallbackLocale, // fallback language
    messages: {}, // no translations loaded until the user logs in.
    numberFormats: {}, // no number formats loaded until the user logs in.
    silentTranslationWarn: true,
    silentFallbackWarn: true,
    datetimeFormats: {}, // no date formats loaded until the user logs in.
    globalInjection: true, // https://github.com/intlify/vue-i18n-next/issues/350#issuecomment-782007971
});

// Default tranlsations available before the users logs in
i18n.global.setLocaleMessage('en', {
    footer: {
        manageCookiesButtonTitle: 'Manage Cookies',
        termsOfUse: 'Terms of Use',
        privacyNote: 'Privacy Notice',
        cookieNotice: 'Cookie Notice',
        solutionFrom: 'A solution from',
    },
    errorPage: {
        403: {
            title: 'Unauthorised',
            description: 'You are not authorised to view the resource',
        },
        404: {
            title: 'Not found',
            description: "The resource you are looking for doesn't exist",
        },
    },
    maintenancePage: {
        title: 'Hierarchy changes are being applied',
        description:
            'The promotion planner is temporarily unavailable while hierarchy changes are applied. It will be available again once the hierarchy changes are complete.',
    },
});

const n = function(
    numberFormatKey,
    value,
    { usePlaceholder = false, placeholder = '-', useAbsoluteValue = false } = {}
) {
    if (isNil(value) && usePlaceholder) return placeholder;

    // Use translations to get the format
    let format = i18n.global.t(numberFormatKey);

    // It is possible for the returned value to point to a key in our defaults
    // As we may want some clients to still have the default value whenever that changes
    // For docs on how this works see: https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/1428127889/Number+Localisation
    if (i18n.global.te && i18n.global.te(`numbers.default.${format}`)) {
        format = i18n.global.t(`numbers.default.${format}`);
    }

    // Use numeralJS to convert value using locale and format and return it
    return numeral(useAbsoluteValue ? Math.abs(value) : value).format(format);
};

// to use it $n(...) in components in old way
// vue-i18n v9 adds mixin in such way: this.$n = (...args) => this.$i18n.n(...args);
// $i18n.n installed from i18n.global.n
i18n.global.n = n;
i18n.n = n;

// i18n.te(key) function checks whether the key exists, but it only checks out a default locale,
// while i18n.t function checks both default and fallback locales. So the tanslation may exist on
// fallback locale but construction if(te(key)) t(key) would behave as if the key does not exist.
// i18n.translationExists applies i18n.te function on both default and fallback locales and checks
// if Translation Exists on Any of the Locales.
i18n.translationExists = function(key) {
    if (!key) {
        return false;
    }
    return (
        i18n.global.te(key, i18n.global.locale) || i18n.global.te(key, i18n.global.fallbackLocale)
    );
};

const resolveMoment = moment => {
    if (typeof moment === 'function') {
        return moment;
    }

    if (typeof moment?.default === 'function') {
        return moment.default;
    }

    return momentLib;
};

const d = function(
    dateFormatKey,
    date,
    moment,
    { usePlaceholder = false, placeholder = '-' } = {}
) {
    if (isNil(date) && usePlaceholder) return placeholder;
    const momentInstance = resolveMoment(moment);
    return momentInstance(date).format(i18n.global.t(dateFormatKey));
};
i18n.global.d = d;
i18n.d = d;

// Converts to UTC before formatting as local , inserting '2021-01-17' gives '2021-01-16' (23:00h) if your local time is positive eg. UTC+1
i18n.dUTC = function(
    dateFormatKey,
    date,
    moment,
    { usePlaceholder = false, placeholder = '-' } = {}
) {
    if (isNil(date) && usePlaceholder) return placeholder;
    const momentInstance = resolveMoment(moment);
    return momentInstance(date)
        .utc()
        .format(i18n.global.t(dateFormatKey));
};

// Parse date as UTC explicitly before formatting, inserting '2021-01-17' gives '2021-01-17' (0:00h)
i18n.eUTC = function(
    dateFormatKey,
    date,
    moment,
    { usePlaceholder = false, placeholder = '-' } = {}
) {
    if (isNil(date) && usePlaceholder) return placeholder;
    const momentInstance = resolveMoment(moment);
    return momentInstance.utc(date).format(i18n.global.t(dateFormatKey));
};

// Need to pick out specifc functions and add to the exported i18n due to vue3 upgrade
const globalI18n = pick(i18n.global, ['t', 'setLocaleMessage']);
export default { ...i18n, ...globalI18n };
